import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ClientService, IsoService } from '@core/services';
import { OiqFormBase } from '@oiq-ui-sdk/form';
import { map, Observable, switchMap } from 'rxjs';
import { AddClientContactFormArray, OiqClientContactForm } from './client-contact-form';

/**
 * Form class for Client Search Dialog
 */
export class OiqClientCreateForm extends OiqFormBase {
  private _selectedIso: any;
  private _isoUsers: Array<any> = [];
  private _contacts = new AddClientContactFormArray();
  private _whiteLabelId: string;

  public isoId$ = this.get('isoId').valueChanges;

  override set configuration(v: any) {
    super.configuration = { ...v };
    this._whiteLabelId = v.whiteLabelId;
  }

  override get configuration() {
    return super.configuration;
  }

  constructor(
    private _clientService?: ClientService,
    private _isoService?: IsoService
  ) {
    super({
      // Basic client info
      id: new FormControl<number>(null),
      name: new FormControl<string>('', [Validators.required]),
      // KEEP: businessDescription is not yet used
      // businessDescription: new FormControl(''),
      legalName: new FormControl(''),
      dbaName: new FormControl(''),
      type: new FormControl('business'),
      ein: new FormControl(''), // Plain field, no validator
      email: new FormControl(''),
      phoneNumber: new FormControl(''),
      cellNumber: new FormControl(''),
      startDate: new FormControl(null),
      sicCode: new FormControl(''),
      businessType: new FormControl(''),
      incorporationState: new FormControl(''),
      grossMonthlySales: new FormControl(null),
      incorporationDate: new FormControl(null),
      lengthOfOwnership: new FormControl(null),
      websiteUrl: new FormControl(''),

      // ISO info
      isoId: new FormControl<number>(null),
      isoSalesRepresentativeId: new FormControl<number>(null),

      // Business address
      address: new FormGroup({
        address1: new FormControl(''),
        address2: new FormControl(''),
        city: new FormControl(''),
        state: new FormControl(''),
        zipCode: new FormControl(''),
        country: new FormControl('US'),
      }),

      // Trade references (usually empty)
      tradeReferences: new FormControl([]),
    });
    this.controls.isoSalesRepresentativeId.disable();
    this.controls.isoId.valueChanges.subscribe(isoId => {
      !isoId
        ? this.controls.isoSalesRepresentativeId.disable()
        : this.controls.isoSalesRepresentativeId.enable();
    });
    this.addControl('clientContacts', this._contacts);
  }

  /**
   * Create a contact form group
   */
  protected createContactFormGroup(isPrimary: boolean): OiqClientContactForm {
    return new OiqClientContactForm(isPrimary);
  }

  // Getters and setters for properties
  get selectedIso(): any {
    return this._selectedIso;
  }

  set selectedIso(value: any) {
    this._selectedIso = value;
  }

  get isoUsers(): Array<any> {
    return this._isoUsers;
  }

  set isoUsers(value: Array<any>) {
    this._isoUsers = value;
    if (this._isoUsers.length > 0) {
      this._isoUsers = this._isoUsers.sort((a, b) => a.firstName.localeCompare(b.firstName));
    }
  }

  // FormArray getter
  get clientContacts(): AddClientContactFormArray {
    return this.get('clientContacts') as AddClientContactFormArray;
  }

  /**
   * Add a new contact to the form
   */
  addContact(isPrimary: boolean = false): void {
    this._contacts.add(isPrimary);
  }

  /**
   * Remove a contact from the form
   */
  removeContact(clientContactIndex: number): void {
    this.clientContacts.removeAt(clientContactIndex);
    this.disableControlIfThereIsSingleContact();
  }

  /**
   * Check if both primary owner and primary contact are selected
   */
  arePrimaryOwnerAndPrimaryContactSelected(): boolean {
    let isTherePrimaryContact = false;
    let isTherePrimaryOwner = false;
    this.getRawValue().clientContacts.forEach(clientContact => {
      if (clientContact.isPrimaryContact) {
        isTherePrimaryContact = true;
      }
      if (clientContact.primary) {
        isTherePrimaryOwner = true;
      }
    });
    return isTherePrimaryOwner && isTherePrimaryContact;
  }

  /**
   * Check if at least one value is selected for each contact
   */
  isAtLeastOneValueSelectedForContact(): boolean {
    let validSelectedContactControls = 0;
    this.getRawValue().clientContacts.forEach(clientContact => {
      if (clientContact.isPrimaryContact || clientContact.primary || clientContact.isOwner) {
        validSelectedContactControls++;
      }
    });
    return validSelectedContactControls === this.getRawValue().clientContacts.length;
  }

  /**
   * Set an ISO for the form
   */
  setIsoData(iso: any): void {
    this.selectedIso = iso || null;
    if (!iso) {
      this.get('isoId').setValue(null);
      return;
    }
    this.get('isoId').setValue(iso.id);
  }

  /**
   * Prepare client data for submission
   */
  override get toPayload(): any {
    const clientForm = this.getRawValue();

    // Format the contacts properly
    const formattedContacts = this.clientContacts.toPayload;

    // Check if business address has values
    const hasBusinessAddress =
      clientForm.address &&
      (clientForm.address.address1 ||
        clientForm.address.city ||
        clientForm.address.state ||
        clientForm.address.zipCode);

    // Create the base payload
    const payload = {
      name: clientForm.name,
      // keep businessDescription for now, it's not used yet'
      // businessDescription: clientForm.businessDescription,
      type: clientForm.type,
      legalName: clientForm.legalName || clientForm.name,
      dbaName: clientForm.dbaName,
      email: clientForm.email,
      phoneNumber: clientForm.phoneNumber,
      cellNumber: clientForm.cellNumber,
      startDate: clientForm.startDate,
      sicCode: clientForm.sicCode || '',
      businessType: clientForm.businessType,
      incorporationState: clientForm.incorporationState,
      grossMonthlySales: clientForm.grossMonthlySales,
      incorporationDate: clientForm.incorporationDate,
      lengthOfOwnership: clientForm.lengthOfOwnership,
      websiteUrl: clientForm.websiteUrl,
      isoId: clientForm.isoId,
      whiteLabelId: this._whiteLabelId,
      isoSalesRepresentativeId: clientForm.isoSalesRepresentativeId,
      tradeReferences: clientForm.tradeReferences || [],
      contacts: formattedContacts.filter((r: any) => !r.isOwner),
      ownerContacts: formattedContacts.filter((r: any) => r.isOwner),
    };

    // handele addresses only add if it exists and is not 'N/A'
    if (hasBusinessAddress) {
      payload['addresses'] = [clientForm.address];
    }

    // Handle EIN - only add if it exists and is not 'N/A'
    if (clientForm.ein && clientForm.ein !== 'N/A' && clientForm.ein.trim() !== '') {
      payload['ein'] = clientForm.ein;
    }

    return payload;
  }

  protected override requestCreate(): Observable<any> {
    return this._clientService.create(this.toPayload).pipe(
      switchMap((_client: any) => {
        const primaryContact = this.getRawValue().clientContacts.filter(
          (r: any) => r.isPrimaryContact
        )[0];
        const resPrimaryContact = _client.contacts.filter((contact: any) => {
          return (
            contact.firstName == primaryContact.firstName &&
            contact.lasttName == primaryContact.lasttName
          );
        })[0];

        return this._clientService
          .setPrimaryContact(_client.id, resPrimaryContact.id)
          .pipe(map(() => _client));
      })
    );
  }

  /**
   * Disable control if there is a single contact
   */
  private disableControlIfThereIsSingleContact(): void {
    if (this.clientContacts.controls.length === 1) {
      this.clientContacts.controls[0].patchValue({
        isPrimaryContact: true,
        isOwner: true,
        primary: true,
      });
      this.clientContacts.controls[0]?.get('isPrimaryContact')?.disable();
      this.clientContacts.controls[0]?.get('isOwner')?.disable();
      this.clientContacts.controls[0]?.get('primary')?.disable();
    }
  }

  /**
   * Validator using business logic methods
   */
  static contactValidationValidator(form: FormGroup): { [key: string]: any } | null {
    const formInstance = form as OiqClientCreateForm;

    if (!formInstance.arePrimaryOwnerAndPrimaryContactSelected()) {
      return {
        contactsValidationErrors: 'At least one Primary Contact or Primary Owner must be selected',
      };
    }

    if (!formInstance.isAtLeastOneValueSelectedForContact()) {
      return { contactsValidationErrors: 'Each contact must have at least one role selected' };
    }

    return null;
  }

  reloadIsoUsers(): void {
    this.get('isoId').updateValueAndValidity();
  }
}
